import React from "react";
import {
    Navigate,
    useNavigate,
    useLocation,
    Routes,
    Route,
} from "react-router-dom";
import ConfigureCredentials from "./ConfigureCredentials";
import ClientPreferences from "./ClientPreferences";
import ARIPreferences from "./ARIPreferences";
import ContentAPI from "./ContentAPI";
import { Card, CardBody, CardTitle } from "reactstrap";
import "./APITesting.css";

const STEPS = {
    CONFIGURE_CREDENTIALS: "configure-credentials",
    CLIENT_PREFERENCES: "client-preferences",
    ARI_PREFERENCES: "ari-preferences",
    CONTENT_API: "content-api",
};

const steps = [
    {
        title: "Configure Credentials",
        description: "Set up API credentials",
        path: STEPS.CONFIGURE_CREDENTIALS,
    },
    {
        title: "Content Settings",
        description: "Set configuration preferences",
        path: STEPS.CLIENT_PREFERENCES,
    },
    {
        title: "ARI Settings",
        description: "Set ARI preferences",
        path: STEPS.ARI_PREFERENCES,
    },
    {
        title: "Content API",
        description: "Test API interactions",
        path: STEPS.CONTENT_API,
    },
];

const APITesting = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const currentPath = location.pathname.split("/").pop();

    const getCurrentStepIndex = () => {
        // Handle content-api and its nested routes
        if (
            currentPath === "content-api" ||
            currentPath === "push" ||
            currentPath === "audit"
        ) {
            return steps.findIndex((step) => step.path === STEPS.CONTENT_API);
        }
        return steps.findIndex((step) => step.path === currentPath);
    };

    // Add effect to handle initial navigation
    React.useEffect(() => {
        if (currentPath === "api-testing") {
            navigate(`/api-testing/${STEPS.CONFIGURE_CREDENTIALS}`);
        }
    }, [currentPath, navigate]);

    return (
        <div className="api-testing">
            <Card>
                <CardBody>
                    <CardTitle tag="h3" className="mb-4 main-title">
                        API Testing
                    </CardTitle>
                    <div className="steps-container">
                        {steps.map((step, index) => (
                            <div
                                key={step.title}
                                className={`step-indicator ${
                                    index === getCurrentStepIndex()
                                        ? "active"
                                        : index < getCurrentStepIndex()
                                        ? "completed"
                                        : ""
                                }`}
                            >
                                <div className="step-number">{index + 1}</div>
                                <div className="step-text">
                                    <span className="step-label">
                                        {step.title}
                                    </span>
                                    <span className="step-description">
                                        {step.description}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="step-content">
                        <Routes>
                            <Route
                                path={STEPS.CONFIGURE_CREDENTIALS}
                                element={
                                    <ConfigureCredentials
                                        onNext={() =>
                                            navigate(
                                                `/api-testing/${STEPS.CLIENT_PREFERENCES}`,
                                            )
                                        }
                                    />
                                }
                            />
                            <Route
                                path={STEPS.CLIENT_PREFERENCES}
                                element={
                                    <ClientPreferences
                                        onBack={() =>
                                            navigate(
                                                `/api-testing/${STEPS.CONFIGURE_CREDENTIALS}`,
                                            )
                                        }
                                        onNext={() =>
                                            navigate(
                                                `/api-testing/${STEPS.ARI_PREFERENCES}`,
                                            )
                                        }
                                        prefType="client"
                                    />
                                }
                            />
                            <Route
                                path={STEPS.ARI_PREFERENCES}
                                element={
                                    <ARIPreferences
                                        onBack={() =>
                                            navigate(
                                                `/api-testing/${STEPS.CLIENT_PREFERENCES}`,
                                            )
                                        }
                                        onNext={() =>
                                            navigate(
                                                `/api-testing/${STEPS.CONTENT_API}`,
                                            )
                                        }
                                        prefType="ari"
                                    />
                                }
                            />
                            <Route
                                path={`${STEPS.CONTENT_API}/*`}
                                element={
                                    <ContentAPI
                                        onBack={() =>
                                            navigate(
                                                `/api-testing/${STEPS.ARI_PREFERENCES}`,
                                            )
                                        }
                                        onFinish={() => navigate("/")}
                                    />
                                }
                            />
                            <Route
                                path="*"
                                element={
                                    <Navigate
                                        to={`/api-testing/${STEPS.CONFIGURE_CREDENTIALS}`}
                                        replace
                                    />
                                }
                            />
                        </Routes>
                    </div>
                </CardBody>
            </Card>
        </div>
    );
};

export default APITesting;
